import { useEffect, useState } from 'react';
import { useParams, useNavigate, useLocation, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { verifyCommit, computeCoinResult } from '../../utils/fairness';

const CoinLabel = ({ value, t }) => {
    if (value === 0) return t('game.skull');
    if (value === 1) return t('game.bolt');
    return '—';
};

function CopyButton({ value, t }) {
    const [copied, setCopied] = useState(false);
    const onClick = async () => {
        try {
            await navigator.clipboard.writeText(value);
            setCopied(true);
            setTimeout(() => setCopied(false), 1500);
        } catch {}
    };
    return (
        <button onClick={onClick} className="ml-2 text-[10px] text-cyber-dim hover:text-cyan-400 underline">
            {copied ? t('verify.copied') : t('verify.copy')}
        </button>
    );
}

export default function VerifyRound() {
    const { roundId } = useParams();
    const { t } = useTranslation();
    const navigate = useNavigate();
    const location = useLocation();
    // react-router v6 sets location.key='default' only when this is the first
    // entry in the SPA history — i.e. the user loaded /verify/<id> directly
    // (shared link, new tab). In that case browser-back would leave the app,
    // so fall back to a static /game link; otherwise go back to where they
    // came from (profile, admin, etc.) preserving scroll + state.
    const canGoBack = location.key !== 'default';
    const [round, setRound] = useState(null);
    const [error, setError] = useState(null);
    const [loading, setLoading] = useState(true);
    const [checks, setChecks] = useState(null);
    const [verifying, setVerifying] = useState(false);

    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                const res = await fetch(`/api/games/${roundId}`);
                const data = await res.json();
                if (cancelled) return;
                if (!res.ok) throw new Error(data.error || t('verify.loadFailed'));
                setRound(data);
            } catch (e) {
                if (!cancelled) setError(e.message);
            } finally {
                if (!cancelled) setLoading(false);
            }
        })();
        return () => { cancelled = true; };
    }, [roundId, t]);

    const runVerify = async () => {
        if (!round) return;
        setVerifying(true);
        try {
            const serverOk = await verifyCommit(round.serverSeed, round.serverCommit);
            const playerAOk = await verifyCommit(round.playerASeed, round.playerACommit);
            const playerBOk = await verifyCommit(round.playerBSeed, round.playerBCommit);
            const computed = await computeCoinResult(round.serverSeed, round.playerASeed, round.playerBSeed);
            const coinOk = computed === round.coinResult;

            let winnerOk = true;
            const choiceA = round.playerAChoice;
            const choiceB = round.playerBChoice;
            const coin = round.coinResult;
            let expectedWinnerId = null;
            if (choiceA === choiceB) {
                expectedWinnerId = null;
            } else if (choiceA === coin) {
                expectedWinnerId = round.playerA?.id || null;
            } else if (choiceB === coin) {
                expectedWinnerId = round.playerB?.id || null;
            }
            winnerOk = (expectedWinnerId === (round.winner?.id || null));

            setChecks({ serverOk, playerAOk, playerBOk, coinOk, winnerOk, computedCoin: computed });
        } finally {
            setVerifying(false);
        }
    };

    if (loading) {
        return (
            <div className="min-h-screen flex items-center justify-center text-cyber-dim tracking-widest text-xs">
                {t('verify.loading')}
            </div>
        );
    }

    if (error) {
        return (
            <div className="min-h-screen flex items-center justify-center text-red-400 tracking-widest text-xs">
                {error}
            </div>
        );
    }

    const isCompleted = round.status === 'completed';
    const isCancelled = round.status === 'cancelled';

    const CheckIcon = ({ ok }) =>
        ok ? <span className="text-green-400">✓</span> : <span className="text-red-400">✕</span>;

    return (
        <div className="min-h-screen p-6 max-w-3xl mx-auto text-cyber-dim text-xs">
            <div className="mb-4">
                {canGoBack ? (
                    <button onClick={() => navigate(-1)} className="text-cyber-dim hover:text-cyan-400">
                        {t('common.back')}
                    </button>
                ) : (
                    <Link to="/game" className="text-cyber-dim hover:text-cyan-400">{t('common.back')}</Link>
                )}
            </div>
            <h1 className="text-lg tracking-widest text-cyan-400 mb-4">{t('verify.title')}</h1>

            <details className="mb-4 border border-cyber-dim/40 p-3 group">
                <summary className="cursor-pointer text-cyan-400 select-none list-none flex items-center justify-between">
                    <span>{t('verify.howItWorksToggle')}</span>
                    <span className="text-cyber-dim text-[10px] tracking-widest group-open:hidden">[ + ]</span>
                    <span className="text-cyber-dim text-[10px] tracking-widest hidden group-open:inline">[ − ]</span>
                </summary>
                <div className="mt-3 space-y-3 leading-relaxed">
                    <p>{t('verify.intro')}</p>

                    <div>
                        <div className="text-cyan-400 mb-1">{t('verify.howStepsTitle')}</div>
                        <ol className="list-decimal pl-5 space-y-1">
                            <li>{t('verify.howStep1')}</li>
                            <li>{t('verify.howStep2')}</li>
                            <li>{t('verify.howStep3')}</li>
                            <li>{t('verify.howStep4')}</li>
                        </ol>
                    </div>

                    <div>
                        <div className="text-cyan-400 mb-1">{t('verify.quickCheckTitle')}</div>
                        <p>{t('verify.quickCheckBody')}</p>
                    </div>

                    <div>
                        <div className="text-cyan-400 mb-1">{t('verify.manualCheckTitle')}</div>
                        <p className="mb-2">{t('verify.manualCommitBody')}</p>
                        <pre className="bg-black/40 border border-cyber-dim/30 p-2 text-[10px] overflow-x-auto whitespace-pre"><code>{`echo -n <seed_hex> | xxd -r -p | sha256sum`}</code></pre>
                        <p className="mt-3 mb-2">{t('verify.manualCoinBody')}</p>
                        <pre className="bg-black/40 border border-cyber-dim/30 p-2 text-[10px] overflow-x-auto whitespace-pre"><code>{`echo -n "<serverSeed><playerASeed><playerBSeed>" | sha256sum
# take first 8 hex chars  →  to decimal  →  mod 2
# 0 = ${t('game.skull')}, 1 = ${t('game.bolt')}`}</code></pre>
                        <p className="mt-3">{t('verify.manualWinnerBody')}</p>
                    </div>

                    <p className="text-cyber-dim/80">{t('verify.howFooter')}</p>
                </div>
            </details>

            <div className="mb-4 space-y-1">
                <div><span className="text-cyber-dim">{t('verify.roundId')}:</span> <span className="text-white">{round.id}</span><CopyButton value={round.id} t={t} /></div>
                <div><span className="text-cyber-dim">{t('verify.roundNumber')}:</span> <span className="text-white">#{round.roundNumber}</span></div>
                <div><span className="text-cyber-dim">{t('verify.status')}:</span> <span className="text-white">{t(`verify.statusLabel.${round.status}`, { defaultValue: round.status })}</span></div>
                <div><span className="text-cyber-dim">{t('verify.bet')}:</span> <span className="text-white">{round.betAmount}</span></div>
                <div><span className="text-cyber-dim">{t('verify.playerA')}:</span> <span className="text-white">{round.playerA?.name || '—'}</span></div>
                <div><span className="text-cyber-dim">{t('verify.playerB')}:</span> <span className="text-white">{round.playerB?.name || '—'}</span></div>
            </div>

            <div className="mb-4 border border-cyber-dim p-3">
                <div className="text-cyan-400 mb-2">{t('verify.commits')}</div>
                <div className="break-all mb-1"><span>{t('verify.serverCommit')}:</span> <span className="text-white">{round.serverCommit}</span><CopyButton value={round.serverCommit} t={t} /></div>
                <div className="break-all mb-1"><span>{t('verify.playerACommit')}:</span> <span className="text-white">{round.playerACommit}</span><CopyButton value={round.playerACommit} t={t} /></div>
                <div className="break-all"><span>{t('verify.playerBCommit')}:</span> <span className="text-white">{round.playerBCommit}</span><CopyButton value={round.playerBCommit} t={t} /></div>
            </div>

            {isCancelled && (
                <div className="mb-4 p-3 border border-red-400 text-red-400">{t('verify.cancelled')}</div>
            )}

            {isCompleted && (
                <>
                    <div className="mb-4 border border-cyber-dim p-3">
                        <div className="text-cyan-400 mb-2">{t('verify.seeds')}</div>
                        <div className="break-all mb-1"><span>{t('verify.serverSeed')}:</span> <span className="text-white">{round.serverSeed}</span><CopyButton value={round.serverSeed} t={t} /></div>
                        <div className="break-all mb-1"><span>{t('verify.playerASeed')}:</span> <span className="text-white">{round.playerASeed}</span><CopyButton value={round.playerASeed} t={t} /></div>
                        <div className="break-all"><span>{t('verify.playerBSeed')}:</span> <span className="text-white">{round.playerBSeed}</span><CopyButton value={round.playerBSeed} t={t} /></div>
                    </div>

                    <div className="mb-4 border border-cyber-dim p-3 space-y-1">
                        <div>{t('verify.playerAChoice')}: <span className="text-white"><CoinLabel value={round.playerAChoice} t={t} /></span></div>
                        <div>{t('verify.playerBChoice')}: <span className="text-white"><CoinLabel value={round.playerBChoice} t={t} /></span></div>
                        <div>{t('verify.coinResult')}: <span className="text-white"><CoinLabel value={round.coinResult} t={t} /></span></div>
                        <div>{t('verify.outcome')}: <span className="text-white">{round.isTie ? t('verify.tie') : (round.winner?.name || '—')}</span></div>
                    </div>

                    <button
                        onClick={runVerify}
                        disabled={verifying}
                        className="px-4 py-2 border border-cyan-400 text-cyan-400 hover:bg-cyan-400/10 disabled:opacity-50"
                    >
                        {verifying ? t('verify.verifying') : t('verify.runVerify')}
                    </button>

                    {checks && (
                        <div className="mt-4 border border-cyber-dim p-3 space-y-1">
                            <div><CheckIcon ok={checks.serverOk} /> {t('verify.check.server')}</div>
                            <div><CheckIcon ok={checks.playerAOk} /> {t('verify.check.playerA')}</div>
                            <div><CheckIcon ok={checks.playerBOk} /> {t('verify.check.playerB')}</div>
                            <div><CheckIcon ok={checks.coinOk} /> {t('verify.check.coin', { expected: round.coinResult, computed: checks.computedCoin })}</div>
                            <div><CheckIcon ok={checks.winnerOk} /> {t('verify.check.winner')}</div>
                        </div>
                    )}
                </>
            )}
        </div>
    );
}
